/**
 * The design system's primitives.
 *
 * Everything the two surfaces share. The rule that shapes all of it: **colour
 * is never the only carrier of state.** Every status has a second, non-colour
 * channel — a link-line stroke pattern borrowed from PCB convention, a position,
 * a glyph, or a label — so the interface survives colour-blindness, greyscale
 * printing and a tired 2am shift.
 *
 * Corners stay square. Circles are reserved for nodes, terminals and status.
 */

import type { ReactNode } from "react";

/* -------------------------------------------------------------------------
   位号 — reference designators
   The club's identity system. Every department, member and project gets one,
   and each always ships with a Chinese label so the code is an anchor, never a
   comprehension tax.
   ------------------------------------------------------------------------- */

export type Designator = "R" | "C" | "U" | "J" | "L" | "F" | "Y" | "D" | "TP";

export function designator(prefix: Designator, index: number): string {
  return `${prefix}${String(index).padStart(2, "0")}`;
}

export function DesignatorTag({
  code,
  label,
  size = "md",
}: {
  code: string;
  label?: string;
  size?: "sm" | "md";
}) {
  return (
    <span className="inline-flex items-baseline gap-2 whitespace-nowrap">
      <span
        className={`data shrink-0 border border-copper/50 text-copper ${
          size === "sm" ? "px-1.5 py-px text-[10px]" : "px-2 py-0.5 text-[11px]"
        }`}
      >
        {code}
      </span>
      {label ? (
        <span className={`silk-label ${size === "sm" ? "text-[10px]" : "text-[11px]"} text-silk-dim`}>
          {label}
        </span>
      ) : null}
    </span>
  );
}

/* -------------------------------------------------------------------------
   电阻色环 — resistor colour bands, used as a real encoding
   Digit 1-2: value. Multiplier. Tolerance. This is the actual code, not a
   decoration, which is why it doubles as a department's identity.
   ------------------------------------------------------------------------- */

const BAND_COLORS: Record<string, string> = {
  "0": "#1a1a1a",
  "1": "#6b3b1e",
  "2": "#c0392b",
  "3": "#d97b28",
  "4": "#d9c02a",
  "5": "#3f9142",
  "6": "#2f6fb5",
  "7": "#7a4bb5",
  "8": "#9aa0a6",
  "9": "#1c1c1e",
};

/** Deterministic band sequence from a string, so a department keeps its colours. */
export function colorBands(seed: string, count = 3): string[] {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  const bands: string[] = [];
  for (let index = 0; index < count; index += 1) {
    bands.push(BAND_COLORS[String(hash % 10)] ?? "#9aa0a6");
    hash = Math.floor(hash / 10) + index * 7 + 1;
  }
  return bands;
}

export function BandStrip({ seed, className = "" }: { seed: string; className?: string }) {
  return (
    <span aria-hidden className={`flex h-4 w-10 shrink-0 overflow-hidden border border-line ${className}`}>
      {colorBands(seed).map((color, index) => (
        <span key={index} className="flex-1" style={{ background: color }} />
      ))}
    </span>
  );
}

/* -------------------------------------------------------------------------
   连线状态 — the second channel for every status
   Real PCB net conventions, drawn rather than coloured:
     unbroken  已接通 / live
     dashed    未布线 / pending — a ratsnest line, a connection that is intended
     broken    已停用 / stopped — an open gap, deliberately left open
     doubled   已选中 / selected
     open-ring 危险操作 / destructive
   ------------------------------------------------------------------------- */

export type NetState = "live" | "pending" | "stopped" | "destructive";

const NET_STROKE: Record<NetState, { strokeDasharray?: string; label: string; tone: string }> = {
  live: { strokeDasharray: undefined, label: "已接通", tone: "text-state-live" },
  pending: { strokeDasharray: "4 3", label: "处理中", tone: "text-state-pending" },
  stopped: { strokeDasharray: "1 4", label: "已断开", tone: "text-state-stopped" },
  destructive: { strokeDasharray: undefined, label: "危险操作", tone: "text-state-danger" },
};

/** The drawn line. Purely structural — it reads with colour removed entirely. */
export function NetLine({ state, className = "" }: { state: NetState; className?: string }) {
  if (state === "destructive") {
    return (
      <svg viewBox="0 0 24 8" aria-hidden className={`h-2 w-6 shrink-0 ${className}`}>
        <circle cx="4" cy="4" r="2.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <path d="M9 4h11" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 8" aria-hidden className={`h-2 w-6 shrink-0 ${className}`}>
      <path
        d="M1 4h22"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        {...(NET_STROKE[state].strokeDasharray
          ? { strokeDasharray: NET_STROKE[state].strokeDasharray }
          : {})}
      />
    </svg>
  );
}

export function StateTag({ state, children }: { state: NetState; children?: ReactNode }) {
  const spec = NET_STROKE[state];
  return (
    <span className={`inline-flex items-center gap-2 ${spec.tone}`}>
      <NetLine state={state} />
      <span className="silk-label text-[10px]">{children ?? spec.label}</span>
    </span>
  );
}

/* -------------------------------------------------------------------------
   Buttons
   Primary is energised copper. Secondary is a hairline boundary that fills on
   hover. Press moves one pixel — the whole tactile vocabulary of the system.
   ------------------------------------------------------------------------- */

export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  // Defaults to "button", not HTML's "submit".
  //
  // A <button> with no type inside a <form> submits that form, so a plain
  // "删除" or "归档" button in a table row was silently recording a ledger
  // entry or firing the form's submit handler. Opting *into* submit is visible
  // in the markup; the reverse is not.
  type = "button",
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
}) {
  const sizes = {
    sm: "px-3 py-1.5 text-[12px]",
    md: "px-4 py-2.5 text-[13px]",
    lg: "px-6 py-3.5 text-[14px]",
  }[size];

  const variants = {
    primary:
      "bg-signal text-silk-inverse border border-signal hover:bg-signal/90 active:translate-y-px",
    secondary:
      "bg-transparent text-silk border border-line-strong hover:bg-silk hover:text-silk-inverse hover:border-silk active:translate-y-px",
    ghost: "bg-transparent text-silk-dim border border-transparent hover:text-silk hover:border-line",
    danger: "bg-transparent text-state-danger border border-state-danger/60 hover:bg-state-danger hover:text-silk-inverse",
  }[variant];

  return (
    <button
      type={type}
      className={`silk-label inline-flex items-center justify-center gap-2 transition-[background-color,color,border-color,transform] duration-150 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-45 ${sizes} ${variants} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------
   Form controls
   Square, hairline, always with a visible label above. Placeholder is never
   the label — the join form and every admin edit need the field named before it
   is touched.
   ------------------------------------------------------------------------- */

export function Field({
  label,
  hint,
  error,
  required,
  htmlFor,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="silk-label mb-1.5 block text-[11px] text-silk-dim">
        {label}
        {required ? <span className="ml-1 text-signal">*</span> : null}
      </span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-[12px] text-state-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-[12px] text-silk-dim">{hint}</span>
      ) : null}
    </label>
  );
}

const controlClass =
  "w-full border border-line bg-ground-sunken px-3 py-2.5 text-[14px] text-silk placeholder:text-silk-dim/60 focus:border-signal focus:outline-none";

export function TextInput({ className = "", ...rest }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={`${controlClass} ${className}`} {...rest} />;
}

export function TextArea({ className = "", ...rest }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`${controlClass} resize-y ${className}`} {...rest} />;
}

export function Select({ className = "", ...rest }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={`${controlClass} ${className}`} {...rest} />;
}

/* -------------------------------------------------------------------------
   Structure
   ------------------------------------------------------------------------- */

export function Plate({
  children,
  className = "",
  title,
  aside,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className={`plate ${className}`}>
      {title || aside ? (
        <header className="flex items-center justify-between gap-4 border-b border-line px-4 py-3">
          <h2 className="silk-label text-[11px] text-silk-dim">{title}</h2>
          {aside}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/** Section heading with the silkscreen crosshair as the alignment mark. */
export function SectionTitle({ children, level = 2 }: { children: ReactNode; level?: 2 | 3 }) {
  const Tag = level === 2 ? "h2" : "h3";
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden className="relative block h-3 w-3">
        <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-copper/60" />
        <span className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-copper/60" />
      </span>
      <Tag className="silk-label text-[11px] text-silk-dim">{children}</Tag>
    </div>
  );
}

/** Empty state. Explicit about the absence rather than rendering a blank. */
export function Empty({ title, detail, action }: { title: string; detail?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-3 border border-dashed border-line px-6 py-10">
      <span aria-hidden className="silk-label text-[10px] text-silk-dim/60">
        NO DATA
      </span>
      <p className="text-[15px] text-silk">{title}</p>
      {detail ? <p className="max-w-prose text-[13px] text-silk-dim">{detail}</p> : null}
      {action}
    </div>
  );
}

export function Spinner({ label = "载入中" }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-[13px] text-silk-dim" role="status">
      <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden>
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
        <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="1.6">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 8 8"
            to="360 8 8"
            dur="0.9s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
      {label}
    </span>
  );
}

/* -------------------------------------------------------------------------
   Figures
   Numbers are data, so they are set in the mono face with tabular figures and
   always carry their unit or caption — a bare large number is unreadable.
   ------------------------------------------------------------------------- */

export function Figure({
  value,
  caption,
  tone = "silk",
}: {
  value: ReactNode;
  caption: string;
  tone?: "silk" | "copper" | "signal";
}) {
  const tones = { silk: "text-silk", copper: "text-copper", signal: "text-signal" };
  return (
    <div className="flex flex-col gap-1">
      <span className={`data text-[28px] leading-none ${tones[tone]}`}>{value}</span>
      <span className="silk-label text-[10px] text-silk-dim">{caption}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Formatting
   Locale-aware and deliberately shared, so a date never appears two ways on
   two screens.
   ------------------------------------------------------------------------- */

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit" }).format(date);
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const seconds = Math.round((then - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("zh-CN", { numeric: "auto" });
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000],
    ["month", 2592000],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return "刚刚";
}

/** Map a domain status onto the non-colour state vocabulary. */
export function statusToNet(status: string): NetState {
  const value = status.toLowerCase();
  if (["approved", "active", "settled", "done", "completed"].includes(value)) return "live";
  if (["pending", "new", "waiting", "processing"].includes(value)) return "pending";
  if (["rejected", "cancelled", "superseded", "disabled", "paused"].includes(value)) return "stopped";
  return "pending";
}

const STATUS_LABELS: Record<string, string> = {
  approved: "已通过",
  rejected: "已驳回",
  pending: "待处理",
  new: "新申请",
  cancelled: "已撤回",
  superseded: "已被取代",
  active: "进行中",
  completed: "已完成",
  paused: "已暂停",
  archived: "已归档",
  planning: "筹备中",
  disabled: "已停用",
  skipped: "已跳过",
  waiting: "待处理",
  public: "公开",
  internal: "内部",
  private: "私有",
};

export function statusLabel(status: string): string {
  return STATUS_LABELS[status.toLowerCase()] ?? status;
}

/** Approval subject types, in the interface's own language. */
const SUBJECT_LABELS: Record<string, string> = {
  join_application: "加入申请",
  usage_request: "领用申请",
  material: "物料",
  fund: "资金",
};

export function subjectLabel(subjectType: string): string {
  return SUBJECT_LABELS[subjectType] ?? subjectType;
}

/**
 * Shorten an opaque id for display: keep the head and tail, drop the middle.
 *
 * A full id is 30 characters of noise. Keeping both ends preserves the parts a
 * human actually compares — the prefix groups it, the tail identifies it — and
 * the full value stays in the `title` for anyone who needs to copy it.
 */
export function shortId(id: string, head = 8, tail = 4): string {
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}
